import axios from "axios";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Blocker,useBlocker, useNavigate } from "react-router-dom";

import apiRoutes from "../constants/api-routes";
import messageLevels from "../constants/message-levels";
import messages from "../constants/messages";
import pages from "../constants/pages";
import useAuthHeader from "../hooks/useAuthHeader";
import useCoAppNavigation from "../hooks/useCoAppNavigation";
import useToastAlert from "../hooks/useToastAlert";
import { RootState } from "../redux/store";
import { selectOrganizationId, selectUser, setOrganizationIdentityProvider, setOrganizationSsoAndScim } from "../redux/userSlice";

interface Step {
	num: number;
	desc: string;
}

interface StepsCompleted {
	[key: string]: boolean;
}

interface SamlClaims {
	email: string;
	surname: string;
	givenName: string;
}

interface TenantDetails {
	secretScimKey?: string;
	tenantId?: string;
	entityId?: string;
	acsUrl?: string;
}

interface MappedRoles {
	[key: string]: string[];
}

interface MappedGroups {
	[key: string]: string[];
}

interface RoleMappingStatus {
	superAdminStatus?: boolean;
	[key: string]: any;
}

export default function useScimWizard() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const currentUser = useSelector(selectUser);
	const organizationId = useSelector(selectOrganizationId);
	const { handleToastAlert } = useToastAlert();
	const { openScimWizard } = useCoAppNavigation();
	const authHeader = useAuthHeader();

	/** API routes require a concrete org id; Redux may still be null briefly. */
	const orgIdForApi = organizationId ?? currentUser.organizationId;

	const [activeStep, setActiveStep] = useState<number>(0);
	const [stepsCompleted, setStepsCompleted] = useState<StepsCompleted>({});
	const [inUseDomains, setInUseDomains] = useState<string[]>([]);
	const [domainError, setDomainError] = useState<string>("");
	const [companyDomains, setCompanyDomains] = useState<string[]>([]);
	const [identityProviderName, setIdentityProviderName] = useState<string>("Azure");
	const [tenantDetails, setTenantDetails] = useState<TenantDetails>({});
	const [samlClaims, setSamlClaims] = useState<SamlClaims>({ email: "", surname: "", givenName: "" });
	const [samlClaimsError, setSamlClaimsError] = useState<SamlClaims>({ email: "", surname: "", givenName: "" });
	const [metadataUrl, setMetadataUrl] = useState<string>("");
	const [metadataError, setMetadataError] = useState<string>("");
	const [mappedRoles, setMappedRoles] = useState<MappedRoles>({});
	const [mappedGroups, setMappedGroups] = useState<MappedGroups>({});
	const [externalGroupsDialogOpen, setExternalGroupsDialogOpen] = useState<boolean>(false);
	const [externalGroupsFound, setExternalGroupsFound] = useState<boolean>(false);
	const [externalGroupsTryAgain, setExternalGroupsTryAgain] = useState<boolean>(false);
	const [noRolesMappedOpen, setNoRolesMappedOpen] = useState<boolean>(false);
	const [superAdminNotMapped, setSuperAdminNotMapped] = useState<boolean>(true);
	const [roleMappingStatus, setRoleMappingStatus] = useState<RoleMappingStatus>({});
	const [roleValidationLoading, setRoleValidationLoading] = useState<boolean>(false);

	const blocker: Blocker = useBlocker(
		({ currentLocation, nextLocation }) =>
			companyDomains.length > 0 && activeStep !== 6 &&
			currentLocation.pathname !== nextLocation.pathname
	);

	const getSteps = (): Step[] => {
		return [
			{ "num": 1, "desc": "Select IdP" },
			{ "num": 2, "desc": "Configure SSO" },
			{ "num": 3, "desc": "Enter SAML Claims" },
			{ "num": 4, "desc": "Configure SCIM" },
			{ "num": 5, "desc": "Map Roles" },
			{ "num": 6, "desc": "Map Groups (optional)" },
			{ "num": 7, "desc": "Confirm Setup" }
		];
	};

	const handleSubmit = (): void => {
		switch (activeStep) {
			case 0:	
				if (companyDomains.length === 0) {
					setDomainError("Required *");
				} else {
					handleNext();
				}
				break;
			case 1:
				handleNext();
				break;
			case 2:
				const hasErrors = validateStep3();
				if (!hasErrors) {
					setSamlAppMetadata();
				}
				break;
			case 3:
				handleExternalGroupRetrieval();
				break;
			case 4:
				validateCurrentUserRoleMappings();
				break;
			case 5:
				handleNext();
				break;
			case 6:
				handleConfigurationConfirmed();
				break;
			default:
				break;
		}
	};

	const getContinueButtonText = (): string => {
		switch (activeStep) {
			case 2:
				return "Save SSO Configuration";
			case 3:
				return "Save SCIM Configuration";
			case 6:
				return "Finish";
			default:
				return "Next";
		}
	};

	const validateStep3 = (): boolean => {
		let hasErrors = false;
		const tempErrors: SamlClaims = { email: "", givenName: "", surname: "" };
		if (samlClaims.email === "") {
			tempErrors.email = "Required *";
			hasErrors = true;
		}
		if (samlClaims.givenName === "") {
			tempErrors.givenName = "Required *";
			hasErrors = true;
		}
		if (samlClaims.surname === "") {
			tempErrors.surname = "Required *";
			hasErrors = true;
		}
		if (metadataUrl === "") {
			setMetadataError("Required *");
			hasErrors = true;
		}
		if (hasErrors) {
			setSamlClaimsError(tempErrors);
		}
		return hasErrors;
	};

	const validateRoleMappings = (mappings: string[][], type: string): void => {
		if (mappings.length === 0) {
			setSuperAdminNotMapped(true);
			return;
		}
		const validateData = {
			mappings: mappings,
			type: type
		};

		axios.post(apiRoutes.validateRoleMappings, validateData, { headers: authHeader })
			.then(({ data }) => {
				if (data.status) {
					setSuperAdminNotMapped(false);
				} else {
					setSuperAdminNotMapped(true);
				}
			})
			.catch(err => {
				console.log(err);
			});
	};

	const validateCurrentUserRoleMappings = (): void => {
		const validateData = {
			mappings: mappedRoles,
			type: "validateCurrentUserRoleStatus",
			descopeUserId: currentUser.descopeUserId
		};
		setRoleValidationLoading(true);
		axios.post(apiRoutes.validateRoleMappings, validateData, { headers: authHeader })
			.then(({ data }) => {
				if (data.superAdminStatus) {
					handleNext();
					setRoleValidationLoading(false);
				} else {
					setNoRolesMappedOpen(true);
					setRoleMappingStatus(data);
					setRoleValidationLoading(false);
				}
			})
			.catch(err => {
				console.log(err);
				setRoleValidationLoading(false);
			});
	};

	const handleCloseRoleValidationModal = (): void => {
		setNoRolesMappedOpen(false);
		setRoleMappingStatus({});
	};

	const handleBack = (): void => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	const handleExternalGroupRetrieval = (): void => {
		if (orgIdForApi == null) {
			console.error("useScimWizard: missing organization id for external groups");
			return;
		}
		axios.get(apiRoutes.getExternalGroups(orgIdForApi), { headers: authHeader })
			.then((res) => {
				if (res.data.length > 0 && !externalGroupsDialogOpen) {
					handleNext();
				} else if (externalGroupsDialogOpen && res.data.length > 0) {
					setExternalGroupsFound(true);
				} else {
					setExternalGroupsDialogOpen(true);
					setTimeout(() => {
						setExternalGroupsTryAgain(false);
					}, 4000);
				}
			})
			.catch((err) => {
				console.log(err);
			});
	};

	const handleClearCurrentConfiguration = (): void => {
		if (orgIdForApi == null) {
			console.error("useScimWizard: missing organization id for clear configuration");
			return;
		}
		axios.delete(apiRoutes.ssoAndScim(orgIdForApi), { headers: authHeader })
			.then(() => {
				blocker.proceed?.();
				dispatch(setOrganizationSsoAndScim(false));
				dispatch(setOrganizationIdentityProvider(""));
			})
			.catch(err => console.error(err));
	};

	const handleConfigurationConfirmed = (): void => {
		const groupsToMap = Object.entries(mappedGroups).filter(([_, value]) => value.length > 0);
		const rolesToMap = Object.entries(mappedRoles).filter(([_, value]) => value.length > 0);
		const unmappedGroups = Object.entries(mappedGroups).filter(([_, value]) => value.length === 0);
		const unmappedRoles = Object.entries(mappedRoles).filter(([_, value]) => value.length === 0);
		const postData = {
			mappedGroups: groupsToMap,
			mappedRoles: rolesToMap,
			unmappedGroups: unmappedGroups,
			unmappedRoles: unmappedRoles,
			identityProviderName: identityProviderName,
			domains: companyDomains
		};
		if (orgIdForApi == null) {
			console.error("useScimWizard: missing organization id for SSO/SCIM save");
			return;
		}
		axios.post(apiRoutes.ssoAndScim(orgIdForApi), postData, { headers: authHeader })
			.then(() => {
				navigate(pages.organizationManagement);
				dispatch(setOrganizationIdentityProvider(identityProviderName));
				dispatch(setOrganizationSsoAndScim(true));
			})
			.catch((err) => {
				handleToastAlert(messageLevels.ERROR, messages.SSO_AND_SCIM_CONFIGURED_ERROR_TOAST_MSG);
				console.log(err);
			});
	};

	const handleMoveUpOneStep = (): void => {
		setActiveStep((activeStep) => activeStep + 1);
	};

	const handleNext = async (): Promise<void> => {
		setStepsCompleted({ ...stepsCompleted, [`${activeStep}`]: true });
		switch (activeStep) {
			case 0:
				handleMoveUpOneStep();
				break;
			case 1:
				handleMoveUpOneStep();
				break;
			case 2:
				handleMoveUpOneStep();
				break;
			case 3:
				setExternalGroupsDialogOpen(false);
				handleMoveUpOneStep();
				break;
			case 4:
				handleMoveUpOneStep();
				setNoRolesMappedOpen(false);
				break;
			case 5:
				handleMoveUpOneStep();
				break;
			case 6:
				handleMoveUpOneStep();
				break;
			default:
				console.log("unknown case");
				break;
		}
	};

	const setSamlAppMetadata = (): void => {
		const metadata = {
			tenantId: tenantDetails.tenantId,
			metadataUrl: metadataUrl,
			emailClaimName: samlClaims.email,
			givenNameClaimName: samlClaims.givenName,
			surnameClaimName: samlClaims.surname,
			domains: companyDomains
		};
		if (orgIdForApi == null) {
			console.error("useScimWizard: missing organization id for SAML metadata");
			return;
		}
		axios.post(apiRoutes.setSamlAppMetadata(orgIdForApi), metadata, { headers: authHeader })
			.then(() => {
				handleNext();
			})
			.catch(err => {
				handleToastAlert(messageLevels.ERROR, messages.SSO_CONFIGURED_ERROR_TOAST_MSG);
				console.log(err);
			});
	};

	const initOrganization = (): void => {
		if (orgIdForApi == null) {
			console.error("useScimWizard: missing organization id for init");
			return;
		}
		axios.get(apiRoutes.getOrganization(orgIdForApi), {
			headers: authHeader
		})
			.then((res) => {
				setTenantDetails({ secretScimKey: res.data.secretScimKey, tenantId: res.data.descopeTenantId, entityId: res.data.ssoMetadata.tenantEntityId, acsUrl: res.data.ssoMetadata.tenantAcsUrl });
				setInUseDomains(res.data.inUseDomains);
			})
			.catch(err => console.error(err));
	};

	const alertUser = (e: BeforeUnloadEvent): void => {
		e.preventDefault();
	};

	const deleteConfiguration = (e: BeforeUnloadEvent | Event): void => {
		if (e.type === "unload") {
			e.preventDefault();
			handleClearCurrentConfiguration();
		}
		if (blocker.state === "blocked") {
			handleClearCurrentConfiguration();
			blocker.proceed?.();
		}
	};

	const handleCancelExit = (): void => {
		openScimWizard(false);
		blocker.reset?.();
	};

	return {
		// State
		activeStep,
		organizationId,
		stepsCompleted,
		noRolesMappedOpen,
		superAdminNotMapped,
		roleMappingStatus,
		roleValidationLoading,

		// State + Setters
		domainError, setDomainError,
		companyDomains, setCompanyDomains,
		identityProviderName, setIdentityProviderName,
		inUseDomains, setInUseDomains,
		tenantDetails, setTenantDetails,
		samlClaims, setSamlClaims,
		samlClaimsError, setSamlClaimsError,
		metadataUrl, setMetadataUrl,
		metadataError, setMetadataError,
		mappedRoles, setMappedRoles,
		mappedGroups, setMappedGroups,
		externalGroupsDialogOpen, setExternalGroupsDialogOpen,
		externalGroupsFound, setExternalGroupsFound,
		externalGroupsTryAgain, setExternalGroupsTryAgain,

		// State Handlers
		getSteps, handleSubmit,
		getContinueButtonText, validateStep3,
		validateRoleMappings, validateCurrentUserRoleMappings,
		handleCloseRoleValidationModal, handleBack,
		handleExternalGroupRetrieval, handleClearCurrentConfiguration,
		handleConfigurationConfirmed, handleMoveUpOneStep,
		handleNext, setSamlAppMetadata,
		initOrganization, alertUser,
		deleteConfiguration, handleCancelExit,

		//Blocker used to prevent user from leaving the page when configuration is not complete
		blocker
	};
}
